import type { ProductEntity } from '@/entities/assortment'
import type {
	PricingCampaignModel,
	ProductsQueryItemModel,
} from '@/shared/generated'
import type { ReactNode } from 'react'

import { useMemo, useState } from 'react'
import {
	createColumnHelper,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { isAggregated, unwrapPricingLine } from '@/entities/assortment'
import { usePricingCampaignQuery } from '@/entities/pricing-campaign'
import {
	competitorsFromTactic,
	findCatalogVariant,
	normalizeTacticParams,
	usePricingRuleCatalogQuery,
} from '@/entities/pricing-rule'

import notAvailable from '@/shared/assets/img/svg/market_tab_not_available.svg'
import { DataTable } from '@/shared/components/tables'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Loader } from '@/shared/components/ui/loader'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useStore } from '@/shared/lib/datasheet/store'

import { useMarketDataQuery } from '../api/queries'
import { alignDatasets, buildMarketView, MY_SERIE_NAME } from '../lib/utils'
import PriceHistoryChart from './PriceHistoryChart'
import { SkuSidebarPricesTable } from './SkuSidebarPricesTable'

type ContentProps = {
	content: ProductEntity
	pcRuleCompetitors: string[]
}
const Content = ({ content, pcRuleCompetitors }: ContentProps) => {
	const { t } = useTranslation()
	const { history, competitive, isLoading, isError } =
		useMarketDataQuery(content)
	// null = user has not toggled yet, fall back to the derived default.
	const [toggled, setToggled] = useState<string[] | null>(null)

	const { chartSeries, marketPrices, defaultVisible } = useMemo(
		() =>
			buildMarketView(
				history?.objects?.prices || [],
				history?.objects?.competitive_data || [],
				competitive?.objects || [],
				pcRuleCompetitors
			),
		[history, competitive, pcRuleCompetitors]
	)
	const visibleOnChart = toggled ?? defaultVisible
	const series = chartSeries.filter(({ name }) => visibleOnChart.includes(name))
	const handleToggleSerie = (name: string) =>
		setToggled(
			visibleOnChart.includes(name)
				? visibleOnChart.filter((i) => i !== name)
				: [...visibleOnChart, name]
		)

	if (isError) {
		return (
			<Empty>
				<EmptyHeader>
					<EmptyMedia>
						<img src={notAvailable} className='h-52' alt='No data' />
					</EmptyMedia>
					<EmptyDescription>
						{t('eastside_market_unavailable')}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}
	const isInRuleOrMyPrice = (label: string) =>
		pcRuleCompetitors.includes(label) || label === MY_SERIE_NAME
	const inRuleData = marketPrices.filter(({ label }) =>
		isInRuleOrMyPrice(label)
	)
	const otherData = marketPrices.filter(
		({ label }) => !isInRuleOrMyPrice(label)
	)

	return (
		<>
			<h5 className='mt-3 mb-2 text-sm'>
				{t('eastside_market_price_history')}
			</h5>
			<div className='flex w-full flex-col'>
				{isLoading ? (
					<div className='relative flex h-52 items-center justify-center'>
						<Loader />
					</div>
				) : (
					<PriceHistoryChart series={alignDatasets(series)} />
				)}
			</div>
			<div className='eastside-panel-content pb-16'>
				<div className='py-4'>
					<h5 className='mb-2 text-sm'>
						{t('app.competitors_used_in_rule', {
							defaultValue: 'Competitors used in the rule',
						})}
					</h5>
					<PricesBlock isLoading={isLoading}>
						<SkuSidebarPricesTable
							rows={inRuleData}
							visibleOnChart={visibleOnChart}
							onToggleSerie={handleToggleSerie}
						/>
					</PricesBlock>
					{otherData.length > 0 ? (
						<>
							<h5 className='mt-3 mb-2 text-sm'>
								{t('app.other_competitors', {
									defaultValue: 'Other competitors',
								})}
							</h5>
							<PricesBlock>
								<SkuSidebarPricesTable
									rows={otherData}
									visibleOnChart={visibleOnChart}
									onToggleSerie={handleToggleSerie}
								/>
							</PricesBlock>
						</>
					) : null}
				</div>
			</div>
		</>
	)
}
type PricesBlockProps = { isLoading?: boolean; children: ReactNode }
const PricesBlock = ({ isLoading, children }: PricesBlockProps) => (
	<div className='border-base max-w-full overflow-hidden rounded-lg border border-solid'>
		{isLoading ? <TableLoadingPlaceholder /> : children}
	</div>
)
type EastsidePanelMarketProps = {
	content: ProductEntity | ProductsQueryItemModel
}
export function MarketTab({ content }: EastsidePanelMarketProps) {
	const { t } = useTranslation()

	const setSkuSidebarContent = useStore((state) => state.setSidebarContent)
	const isSkuSelected = !isAggregated(content as ProductsQueryItemModel)
	const pricingCampaignQuery = usePricingCampaignQuery<PricingCampaignModel>(
		content.fields.pricing_campaign_id ?? '',
		{
			enabled: !!content.fields.pricing_campaign_id,
		}
	)
	const { data: catalog } = usePricingRuleCatalogQuery()
	// Memoized: a fresh array each render would recompute the derived market view.
	const pcRuleCompetitors = useMemo(() => {
		const params = normalizeTacticParams(
			prop('settings.pricing_tactics.params', pricingCampaignQuery.data)
		)

		return competitorsFromTactic(
			findCatalogVariant(catalog?.data ?? [], params),
			params.rule_params
		)
	}, [catalog, pricingCampaignQuery.data])
	if (!isSkuSelected) {
		return (
			<div className='py-4'>
				<h3 className='mb-2 text-sm font-bold'>
					{t('eastside_market_select_sku_pls')}
				</h3>
				<SelectSkuTable
					rows={unwrapPricingLine(content as ProductsQueryItemModel)}
					onSelect={setSkuSidebarContent}
				/>
			</div>
		)
	}

	const sku = content as ProductEntity

	return (
		<Content
			key={`${sku.line_id}:${sku.sku}`}
			content={sku}
			pcRuleCompetitors={pcRuleCompetitors}
		/>
	)
}
const columnHelper = createColumnHelper<ProductEntity>()
function SelectSkuTable({
	rows,
	onSelect,
}: {
	rows: ProductEntity[]
	onSelect: (row: ProductEntity) => void
}) {
	const { t } = useTranslation()

	const columns = useMemo(
		() => [
			columnHelper.accessor('sku', {
				size: 140,
				enableSorting: false,
				header: () => t('sku'),
				cell: (info) => (
					<button
						type='button'
						data-testid='sku-sidebar-select-sku-button'
						className='text-link block w-full cursor-pointer truncate text-left text-sm font-medium text-primary'
						onClick={() => onSelect(info.row.original)}
					>
						{info.getValue()}
					</button>
				),
			}),
			columnHelper.accessor('title', {
				enableSorting: false,
				header: () => t('field_schema_title', { ns: 'fields' }),
				cell: (info) => (
					<div className='truncate text-sm font-medium'>{info.getValue()}</div>
				),
			}),
		],
		[onSelect, t]
	)
	const table = useReactTable<ProductEntity>({
		data: rows,
		columns,
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})

	return (
		<div className='border-base flex max-h-87 w-full flex-col overflow-hidden rounded-lg border border-solid'>
			<DataTable getRowClassName={() => 'h-9 cursor-pointer'} table={table} />
		</div>
	)
}
function TableLoadingPlaceholder() {
	return (
		<div className='flex w-full animate-pulse flex-col space-y-3 p-2'>
			<div className='flex items-center'>
				<Skeleton className='h-3 w-20' />
				<Skeleton className='ml-10 h-3 w-8' />
				<Skeleton className='ml-7 h-3 w-11' />
				<Skeleton className='ml-auto h-3 w-6' />
			</div>
			<div className='flex items-center'>
				<Skeleton className='h-3 w-24' />
				<Skeleton className='ml-6 h-3 w-9' />
				<Skeleton className='ml-6 h-3 w-11' />
				<Skeleton className='ml-auto h-3 w-5' />
			</div>
			<div className='flex items-center'>
				<Skeleton className='h-3 w-20' />
				<Skeleton className='ml-10 h-3 w-11' />
				<Skeleton className='ml-4 h-3 w-11' />
				<Skeleton className='ml-auto h-3 w-4' />
			</div>
			<div className='flex items-center'>
				<Skeleton className='h-3 w-28' />
				<Skeleton className='ml-2 h-3 w-10' />
				<Skeleton className='ml-5 h-3 w-11' />
				<Skeleton className='ml-auto h-3 w-5' />
			</div>
		</div>
	)
}
